import * as React from "react";
import MediaQuery from "react-responsive";

import {observer} from "mobx-react";
import {autorun, configure, reaction} from "mobx"; configure({ enforceActions: "never" });

import "./app.scss";

import {AppConfiguration} from "@/core/config/app-configuration";
import {ModuleConfiguration} from "@/core/config/module-configuration";
import {MapViewerConfiguration} from "@/config/map-viewer-configuration";

import {ErrorBoundary} from "@/core/components/error-boundary/error-boundary";
import esriConfig from "@arcgis/core/config";

import {MapState} from "@/core/map-state";
import {UiState} from "@/core/ui-state";
import {AppState} from "./app-mobx";

import {Header} from "./header/header";
import {Footer} from "./footer/footer";
import Maintenance from "@/core/components/maintenance/maintenance";

import {GeoProxy} from "@/core/geo-proxy";
import {MessageProps, Sidebar as SemanticSidebar} from "semantic-ui-react";

import {BasemapGallery} from "@/core/components/basemap-gallery/basemap-gallery";
import {Sidebar} from "./sidebar/sidebar";

import {MessageConfiguration} from "@/core/config/message-configuration";
import {MessageBox} from "@/core/components/message-box/message-box";
import MessageBlock from "@/core/components/message-block/message-block";
import MessageStartup from "@/core/components/message-startup/message-startup";
import {Popup} from "@/core/components/popup/popup";
import {NavigateFunction} from "react-router";
import CookieBanner from "@/core/components/cookie-banner/cookie-banner";
import SVG from "react-inlinesvg";
import crossIcon from "@/assets/images/layout/cross.svg";
import EsriCompass from "@arcgis/core/widgets/Compass";
import EsriLocate from "@arcgis/core/widgets/Locate";

import {Measurement} from "@/core/components/tools/measurement/measurement";
import {ElevationProfile} from "@/core/components/tools/elevation-profile/elevation-profile";
import {PathUtils} from "@/utils/path-utils";
import {Notfound} from "@/core/components/not-found/not-found";
import {TopicSwitcher} from "@/core/components/topic-switcher/topic-switcher";
import * as arcade from "@arcgis/core/arcade";

import {Authentication} from "@/core/authentication";

import {RisikoanalyseStore} from "@/stores/risikoanalyse-store-mobx";
import {RisikoanalysePopup} from "./risikoanalyse-popup/risikoanalyse-popup";

import {AppStoreProvider} from "@/core/stores/abstract-app-store";
import {TopicConfiguration} from "@/config/topic-configuration";

/* Lazy load Webmap to show loader while importing */
const WebMap = React.lazy(() =>
    import("../../core/geo").then((module) => ({ default: module.WebMap }))
);

export interface AppProps {
    config: AppConfiguration;
    moduleConfig: ModuleConfiguration;
    appStore: AppState;
    location: Location;
    navigate: NavigateFunction;
    params: any;
    searchParams: URLSearchParams;
    setSearchParams: () => void;
}

/**
 * Anwendungsklasse
 */
@observer
export class App extends React.Component<AppProps, any> {
    risikoanalyse: RisikoanalyseStore;

    autorunDisposers: any[] = [];
    tocReadyAutorunDisposers: any;
    appStore: AppState;
    uiStore: UiState;
    mapStore: MapState;

    mapViewerConfig: MapViewerConfiguration;
    appConfig: AppConfiguration;

    appUiRef = React.createRef<HTMLDivElement>();

    stopBrowserNavigationListener: any;
    focusArgs: number[] = [];

    constructor(props: AppProps) {
        super(props);
        this.appStore = this.props.appStore;
        this.uiStore = new UiState();
        this.mapStore = new MapState();
        this.state = {
            northFacing: true,
            alertMessage: [],
            startupMessage: [],
            addClass: []
        };

        this.uiStore.setUiSize(window.innerWidth, window.innerHeight);

        this.mapViewerConfig = this.props.moduleConfig as MapViewerConfiguration;
        this.appConfig = props.config as AppConfiguration;

        // initialize the core application store
        this.risikoanalyse = new RisikoanalyseStore({
            appConfig: this.appConfig,
            mapViewerConfig: this.mapViewerConfig,
            appStore: this.appStore,
            mapStore: this.mapStore,
            uiStore: this.uiStore
        });

        // configure proxy page
        const geoProxy = new GeoProxy(this.appConfig.proxy);
        geoProxy.loadRules();
        esriConfig.request.proxyUrl = this.appConfig.proxy.url;

        // configure assets path
        if (this.appConfig.assetsPath) {
            esriConfig.assetsPath = this.appConfig.assetsPath;
        }
        // configure fonts URL
        if (this.appConfig.fontsUrl) {
            esriConfig.fontsUrl = this.appConfig.fontsUrl;
        }
        // configure GeometryService URL
        if (this.appConfig.geometryServiceUrl) {
            esriConfig.geometryServiceUrl = this.appConfig.geometryServiceUrl;
        }
        // configure portal
        if (this.appConfig.portalUrl) {
            esriConfig.portalUrl = this.appConfig.portalUrl;

            const authentication = new Authentication({
                appConfig: this.appConfig,
                appNamespace: "risikoanalyse"
            });
            // remember authentication to get global access
            props.appStore.setAuthentication(authentication);

            authentication.login().then((credential: __esri.Credential) => {
                props.appStore.setLoggedIn(true, credential);
                this.risikoanalyse.loadApi();
            }).catch(() => {
                props.appStore.setLoggedIn(false);
            });

        } else {
            // eslint-disable-next-line no-console
            console.warn("No portal connection defined.");
        }
        this.mapStore.setAppConfig(this.appConfig);
        this.setTopic();

        this.handleMapLoad = this.handleMapLoad.bind(this);
        this.handleBrowserNavigation = this.handleBrowserNavigation.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }

    /**
     * Load the default topic
     */
    loadDefaultTopic(): void {
        // invalid topic id set on url --> start with default
        const topic = this.mapViewerConfig.getTopicByRoute("/", this.mapViewerConfig.subtopics);
        if (!topic) {
            const msg = "No default topic found in config. Please add '/' for one of the topics in routes array";
            // eslint-disable-next-line no-console
            console.error(msg);
            throw new Error(msg);
        }
        this.appStore.setSubtopic(topic);
    }

    /**
     * Extract route from url and find topic by route
     * Set topic when route matches a topic
     */
    setTopicFromUrl(): void {
        if (this.props.params && this.props.params.subtopic) {
            const topicRoute = this.props.params.subtopic;
            // get topicId from url and set to mobx store
            const topic = this.mapViewerConfig.getTopicByRoute(`/${topicRoute}`, this.mapViewerConfig.subtopics);
            if(topic) {
                this.appStore.setSubtopic(topic);
            } else {
                this.loadDefaultTopic();
            }
        } else {
            this.loadDefaultTopic();
        }
    }

    setTopic(): void {
        const defaultTopic = this.mapViewerConfig.getTopicByRoute("/");
        if (!defaultTopic) {
            const msg = "No default topic found in config. Please add '/' for one of the topics in routes array";
            // eslint-disable-next-line no-console
            console.error(msg);
            throw new Error(msg);
            // if ("basemap" in topicConfig) {
            //     if ("opacity" in topicConfig.basemap) {
            //         this.appConfig.basemapGallery.opacity = topicConfig.basemap.opacity;
            //     }
            //     if ("default" in topicConfig.basemap && topicConfig.basemap.default) {
            //         this.appConfig.basemapGallery.default = topicConfig.basemap.default;
            //     }
            // }
        }
        // Scale und Basemap aus Subtopic übernehmen, falls definiert
        // if(this.props.params?.subtopic){
        //     const subtopic = this.mapViewerConfig.subtopics.find((s)=>
        //         s.routes && s.routes.includes(`/${this.props.params.subtopic}`));
        //     if (subtopic) {
        //         // TODO: Subtopic spezifischer Extent ermöglichen
        //         if ("mapView" in subtopic) {
        //             if ("center" in subtopic.mapView && subtopic.mapView.center) {
        //                 // falls nötig, fehlende spatialReference ergänzen
        //                 if (!("spatialReference" in subtopic.mapView.center)) {
        //                     (subtopic.mapView.center as __esri.Point).spatialReference
        //                                 = new SpatialReference({wkid: 2056});
        //                 }
        //             }
        //             this.appConfig.mapView = ObjectUtils.mergeDeep(this.appConfig.mapView, subtopic.mapView);
        //             if (subtopic.mapView?.scale) {
        //                 if (!("mapView" in topicConfig)) {
        //                     topicConfig.mapView = new MapViewConfiguration();
        //                 }
        //                 topicConfig.mapView.scale = subtopic.mapView.scale;
        //             }
        //         }
        //         // TODO: Subtopic Basemap ermöglichen
        //         if ("basemap" in subtopic) {
        //             if ("opacity" in subtopic.basemap) {
        //                 this.appConfig.basemapGallery.opacity = subtopic.basemap.opacity;
        //             }
        //             if ("default" in subtopic.basemap && subtopic.basemap.default) {
        //                 this.appConfig.basemapGallery.default = subtopic.basemap.default;
        //             }
        //         }
        //     }
        // }
        // Zufälliger Extent aus Bookmarks, falls weder center noch extent definiert wurde
        if(!this.appConfig.mapView.center && !this.appConfig.mapView.extent
            && !this.appConfig.mapView.viewpoint) {
            const {bookmarks} = this.mapViewerConfig;
            const bookmark = bookmarks[Math.floor(Math.random() * bookmarks.length)];
            if(bookmark?.viewpoint) {
                delete this.appConfig.mapView.center;
                delete this.appConfig.mapView.zoom;
                this.appConfig.mapView.viewpoint = bookmark.viewpoint;
                if(this.appConfig.mapView.scale) {
                    this.appConfig.mapView.viewpoint.scale = this.appConfig.mapView.scale;
                    delete this.appConfig.mapView.scale;
                }
            }
        }
        this.appStore.setTopic(defaultTopic);
    }

    /**
     * Extract station ID from url
     * Select station when station ID is present
     */
    selectObjectFromUrl(): void {
        const {params} = this.props;
        if (!params || !params.objectId) {
            return;
        }

        const objectId = params.objectId;
        this.appStore.setObjectId(objectId);
    }
    handleResize(): void {
        this.checkUiSize();
    }
    checkUiSize(): void {
        const uiHeight = this.appUiRef.current?.clientHeight;
        const uiWidth = this.appUiRef.current?.clientWidth;
        const isSmallDevice = (window.innerWidth <= this.mapViewerConfig.breakpoints.md);
        this.uiStore.setUiSize(uiWidth, uiHeight, isSmallDevice);
    }
    componentDidMount(): void {
        const { location, navigate, params, searchParams } = this.props;

        // store route information to mobx store
        this.appStore.setRoute(this.props.location, this.props.params, this.props.navigate);
        this.setTopicFromUrl();
        this.selectObjectFromUrl();

        this.mapStore.setWebMap(this.mapViewerConfig.webMap);

        // rerun until navigate is available
        autorun((locationReaction: any) => {
            const appStoreLocation = this.appStore.location;
            if (!appStoreLocation) {
                return;
            }
            this.handleBrowserNavigation(location, navigate, params);
            this.checkUiSize();
            locationReaction.dispose(); // stop autorun
        });
        autorun((viewReaction: any) => {
            const view = this.mapStore.view;
            if (!view) {
                return;
            }
            this.risikoanalyse.processAdditionalParams(searchParams);
            this.checkUiSize();
            viewReaction.dispose(); // stop autorun
        });
        this.checkUiSize();
        window.addEventListener("resize", this.handleResize);
        this.updateMessages();
        this.autorunDisposers.push(reaction(() => this.appStore.subtopicId, () => {
            this.updateSubtopicConfig();
        }));
        this.autorunDisposers.push(
            reaction(
                () => this.mapStore.activeLayerMetauids,
                () => {
                    this.updateMessages();
                }
            )
        );
    }
    componentDidUpdate(prevProps: AppProps): void {
        const { location, navigate, params } = this.props;
        if(prevProps.location.pathname !== this.props.location.pathname
            || (this.props.location.hash && prevProps.location.hash!==this.props.location.hash)){
            this.appStore = this.props.appStore;
            this.handleBrowserNavigation(location, navigate, params);
        }
    }
    componentWillUnmount(): void {
        // dispose reactions
        this.stopBrowserNavigationListener();
        window.removeEventListener("resize", this.handleResize);
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        if(this.tocReadyAutorunDisposers){
            this.tocReadyAutorunDisposers.dispose();
        }
        if (this.risikoanalyse.LayerSelectorStore.disposeSelectionInitializer) {
            this.risikoanalyse.LayerSelectorStore.disposeSelectionInitializer();
        }
    }

    setSidebarVisible(visible: boolean): void {
        this.uiStore.setSidebarVisible(visible);
    }

    render(): JSX.Element | null {
        let mapProperties: __esri.WebMapProperties | undefined;
        const {maintenance, search} = this.appConfig;
        const {menuVisible, isSidebarVisible, isSmallDevice, uiHeight, uiOffsetTop} = this.uiStore;
        const topicswitcherVisible = this.risikoanalyse.TopicSwitcherStore.visible;
        const {basemapOpacity, status, webMap} = this.mapStore;
        const {northFacing, alertMessages, startupMessage} = this.state;
        if (!this.appStore.topic){
            return <Notfound/>;
        }
        if (webMap){
            mapProperties = webMap as __esri.WebMapProperties;
        }
        let addClass = "geo-menu-" + ((menuVisible && !topicswitcherVisible)? "visible": "hidden");
        addClass += " geo-subtopic-menu-" + (topicswitcherVisible? "visible": "hidden");
        addClass += (this.appStore.topic)? " topic-" + this.appStore.topic.id : "";
        addClass += (this.appStore.subtopic)? " subtopic-" + this.appStore.subtopic.id : "";
        addClass += " " + status;
        addClass += (northFacing? " geo-north-facing": " geo-not-north-facing");
        addClass += (this.uiStore.sidebarHigh)? " geo-sidebar-is_high" : "";
        addClass += " "+this.state.addClass.join(" ");
        addClass += isSmallDevice? " geo-small_device" : "";
        addClass += (basemapOpacity <= 5)? " show-grid": "";
        const cssProperties = { "--uiHeight": uiHeight + "px", "--uiOffsetTop": uiOffsetTop + "px" };
        const loadingLabel = (
            <span>Anwendung&nbsp;
                <span className="pre-loader-topic">{this.appStore.topic.label? this.appStore.topic.label : ""}</span>
                &nbsp;wird geladen&nbsp;…
            </span>);
        return(
            <ErrorBoundary>
                <AppStoreProvider store={this.risikoanalyse}>
                    <SemanticSidebar.Pushable className={addClass} style={cssProperties}>
                        <SemanticSidebar
                            animation="push"
                            onHide={() => this.setSidebarVisible(false)}
                            visible={isSidebarVisible}
                        >
                            <MediaQuery maxWidth={this.mapViewerConfig.breakpoints.md}>
                                <div className="mobilenavigation__header">
                                    <button className="no-focusborder offside-button mobilenavigation__close-button"
                                        type="button"
                                        onClick={() => this.setSidebarVisible(false)}>
                                        <span className="sprite sprite--cross">
                                            <SVG src={crossIcon} />
                                        </span>
                                    </button>
                                </div>
                                <Sidebar
                                    config={this.mapViewerConfig.sidebar}
                                    small={true}
                                />
                            </MediaQuery>
                        </SemanticSidebar>
                        <SemanticSidebar.Pusher id={"map-root"} className={"geo-app-body"}>
                            <React.Suspense fallback={
                                <div className="pre-loader">
                                    <div className="pre-loader-inner">{loadingLabel}</div>
                                </div>
                            }>
                                <div className="pre-loader">
                                    <div className="pre-loader-inner">{loadingLabel}</div>
                                </div>
                                <WebMap
                                    mapProperties={mapProperties}
                                    viewProperties={this.appConfig.mapView as __esri.MapViewProperties}
                                    onLoad={this.handleMapLoad} >
                                </WebMap>
                            </React.Suspense>
                            <div className={"geo-app geo-header-added"} ref={this.appUiRef}>
                                <Maintenance config={maintenance} />
                                <Header view={this.mapStore.view}
                                    appStore={this.appStore}
                                    uiStore={this.uiStore}
                                    mapStore={this.mapStore}
                                    capabilities={["search"]}
                                    appConfig={this.appConfig}
                                    searchConfig={search}
                                    mapViewerConfig={this.mapViewerConfig}
                                />
                                <div className={"geo-ui-root"}>
                                    <div className={"geo-ui"}>
                                        <div className={"geo-ui-inner-container geo-ui-manual-container"}>
                                            {this.mapViewerConfig.popup.disabled? null: (
                                                <Popup config={this.mapViewerConfig.popup}/>)
                                            }
                                            <RisikoanalysePopup risikoanalyse={this.risikoanalyse}
                                                config={this.mapViewerConfig.popup}
                                                mapViewerConfig={this.mapViewerConfig}
                                                appConfig={this.appConfig}
                                                view={this.mapStore.view}
                                                uiStore={this.uiStore}
                                                appStore={this.appStore}
                                                mapStore={this.mapStore} />
                                        </div>
                                        <div className={"geo-ui-inner-container geo-ui-corner-container"}>
                                            <MediaQuery minWidth={(this.mapViewerConfig.breakpoints.md + 1)}>
                                                <Sidebar config={this.mapViewerConfig.sidebar}/>
                                                <TopicSwitcher topics={this.mapViewerConfig.subtopics} />
                                            </MediaQuery>
                                            <div className={"geo-ui-top-left geo-ui-corner"}>
                                                <ElevationProfile config={this.mapViewerConfig.tools?.profile}/>
                                                <Measurement />
                                            </div>
                                            <div className={"geo-ui-top-right geo-ui-corner"}/>
                                            <div className={"geo-ui-bottom-right geo-ui-corner"}>
                                                <BasemapGallery config={this.appConfig.basemapGallery} />
                                            </div>
                                            <div className={"geo-ui-bottom-left geo-ui-corner"}>
                                                { alertMessages?.length > 0 &&
                                                    <div className="geo-messages">{alertMessages}</div>}
                                            </div>
                                            {startupMessage}
                                        </div>
                                    </div>
                                </div>
                                <Footer />
                            </div>
                            <MessageBox store={this.appStore} />
                            <CookieBanner />
                        </SemanticSidebar.Pusher>
                    </SemanticSidebar.Pushable>
                </AppStoreProvider>
            </ErrorBoundary>
        );
    }

    handleMapLoad(map: __esri.Map, view: __esri.MapView | __esri.SceneView): void {
        // TODO: AppState status erst auf complete schalten, wenn alle Widgets geladen sind
        // this.appStore.setState({ status: "complete" });
        if (this.mapViewerConfig.locateButtonEnabled) {
            const locateWidget = new EsriLocate({
                view
            });
            view.ui.add(locateWidget, "top-right");
        }
        // mode zoom buttons to top right
        view.ui.move("zoom", "top-right");
        const compassWidget = new EsriCompass({
            view
        });
        view.ui.add(compassWidget, "top-right");
        view.watch("rotation", (newValue: number)=>{
            this.setState({northFacing: newValue === 0});
        });

        view.closePopup();
    }

    /**
     * Register listener to set topic, when navigation is done with browser back/forward buttons
     */
    handleBrowserNavigation(location: Location, navigate: NavigateFunction,params: any): void {
        // store route information to mobx store
        this.appStore.setRoute(location, params, navigate);
        this.handleSubtopicBrowserNavigation(location, params);
    }
    handleSubtopicBrowserNavigation(location: Location, params: any): void {
        if(!this.appStore.subtopic || !this.appStore.subtopic.routes.includes("/" + params.subtopic)){
            const subtopic = this.mapViewerConfig.getTopicByRoute(
                "/" + params.subtopic, this.mapViewerConfig.subtopics);
            if(subtopic) {
                this.appStore.setSubtopic(subtopic);
            } else {
                if (this.mapViewerConfig.subtopics) {
                    // "forwarden" zu default Subtopic
                    const defaultView = this.mapViewerConfig.topicSwitcher?.default;
                    if (!defaultView) {
                        console.warn("Default Subtopic nicht konfiguriert! (topicSwitcher.default)");
                    }
                    const currentView = this.mapViewerConfig.subtopics?.find((v) => v.id === defaultView);
                    if (!currentView) {
                        console.warn(`Default Subtopic "${defaultView}" nicht gefunden!`);
                    } else {
                        this.appStore.setSubtopic(currentView);
                        this.appStore.setRoutePath(
                            PathUtils.joinPath(this.appStore.topic?.routes[0], currentView?.routes[0]));
                    }
                }
            }
        }
        if(params.objectId !== this.appStore.objectId){
            this.appStore.setObjectId(this.props.params.objectId);
        }
        this.handleLocationHash(location.hash);
    }
    handleLocationHash(hash: string): void {
        if (hash) {
            hash = location.hash.replace("#", "");
            hash = decodeURIComponent(hash);
            if(hash.indexOf("more-")===0 || hash.indexOf("less-")===0) {
                this.handleLessMore(hash);
            }else{
                if(this.mapStore.tocReady){
                    this.risikoanalyse.showObject(hash);
                }else if(!this.tocReadyAutorunDisposers){
                    this.tocReadyAutorunDisposers = autorun((r)=>{
                        if(this.mapStore.tocReady){
                            r.dispose();
                            this.risikoanalyse.showObject(hash);
                        }
                    });
                }
            }
            this.appStore.removeRouteHash();
        }
    }
    handleLessMore(hash: string): void {
        const {addClass} = this.state;
        const target = hash.substring(5);
        if(addClass.indexOf("less-" + target) > -1){
            addClass.splice(addClass.indexOf("less-" + target),1);
        }
        if(addClass.indexOf("more-" + target) > -1){
            addClass.splice(addClass.indexOf("more-" + target),1);
        }
        addClass.push(hash);
        this.setState({addClass});
    }
    updateSubtopicConfig(){
        const subtopic: TopicConfiguration | undefined = this.appStore.mapViewerConfig.subtopics.find((s)=>
            s.id === this.appStore.subtopicId);
        if (subtopic) {
            if ("basemap" in subtopic && subtopic.basemap?.force) {
                if ("opacity" in subtopic.basemap && typeof subtopic.basemap.opacity === "number") {
                    this.mapStore.setBasemapOpacity(subtopic.basemap.opacity as number);
                }
                if ("default" in subtopic.basemap && subtopic.basemap.default) {
                    this.mapStore.setBasemap(subtopic.basemap.default);
                }
            }
        }
    }
    updateMessages(){
        const startupMessage = this.prepareMessagesContent(
            this.mapViewerConfig.meta?.startupMessage?
                (Array.isArray(this.mapViewerConfig.meta.startupMessage)?
                    this.mapViewerConfig.meta.startupMessage: [this.mapViewerConfig.meta.startupMessage]):
                undefined, true);
        Promise.all(startupMessage).then((msgs) => {
            this.setState({startupMessage: msgs.filter((m) => m)});
        });
        const alertMessages = this.prepareMessagesContent(this.mapViewerConfig.meta.alerts);
        Promise.all(alertMessages).then((msgs) => {
            this.setState({alertMessages: msgs.filter((m) => m)});
        });
    }
    prepareMessagesContent(messagesConfig?: MessageConfiguration[], startup=false):
    Array<Promise<JSX.Element | undefined>> {
        const messagePromises: Array<Promise<JSX.Element | undefined>> = [];
        if (messagesConfig && Array.isArray(messagesConfig)) {
            const profile: __esri.Profile = {
                variables: [{
                    name: "$subtopicId",
                    type: "text"
                }, {
                    name: "$activeLayers",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }, {
                    name: "$activeLayersMeta",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }]
            };
            messagesConfig.forEach((message, index) => {
                if (typeof message.condition === "undefined") {
                    messagePromises.push(new Promise((resolve) => {
                        resolve(this.prepareMessageContent(message, index, startup));
                    }));
                } else {
                    if(this.mapStore.activeLayerMetauids) {
                        const activeLayers: string[] = [];
                        this.mapStore.activeLayers.forEach((layerNumbers: number[], layerMeta) => {
                            layerNumbers?.forEach((lNo: number)=>{
                                activeLayers.push(layerMeta + "-" + lNo.toString());
                            });
                        });
                        const arcadeExecutor = arcade.createArcadeExecutor(message.condition, profile);
                        messagePromises.push(new Promise((resolve) => {
                            arcadeExecutor.then((ae) => {
                                const condition = ae.execute({
                                    $activeLayers: activeLayers,
                                    $activeLayersMeta: this.mapStore.activeLayerMetauids,
                                    $subtopicId: (this.appStore.subtopicId? this.appStore.subtopicId: "")
                                });
                                let m: JSX.Element | undefined;
                                if (condition) {
                                    m = this.prepareMessageContent(message, index, startup);
                                }
                                resolve(m);
                            });
                        }));
                    }
                }
            });
        }
        return messagePromises;
    }
    prepareMessageContent(messageConfig: MessageConfiguration, index: number, startup=false): JSX.Element {
        let message: JSX.Element;
        if (startup){
            message = <MessageStartup
                key={index}
                topic={this.appStore.topic.id}
                subtopic={this.appStore.subtopic?.id}
                header={messageConfig.title}
                className={messageConfig.class ? messageConfig.class : ""}
                content={messageConfig.content}
                appConfig={this.appConfig}/>;
        } else {
            message = <MessageBlock
                key={index}
                index={index}
                topic={this.appStore.topic.id}
                subtopic={this.appStore.subtopic?.id}
                header={messageConfig.title}
                className={messageConfig.class ? (messageConfig as MessageProps).class : undefined}
                content={messageConfig.content}
                appConfig={this.appConfig}/>;
        }
        return message;
    }
}
